/*
 * This composable is used to synchronize a value with the browser's local storage.
 *
 * The composable takes an optional namespace as argument that is used to prefix
 * all the keys in the local storage.
 *
 * The options are:
 * - load: if true, the state is loaded from the local storage
 * - preset: if true, the local storage is set with the initial value right away
 * - immediate: if true, the state is saved immediately when the component is mounted
 * - deep: if true, the variable is watched "deeply" i.e. the state is updated even when
 *         a sub-property of the variable changes
 * Raw strings use the separate `syncRawState` helper.
 *
 * Ownership: persistence relies on a watcher, and that watcher runs in its
 * own detached effect scope — it is never bound to the calling component, so
 * calling `syncState` after the first `await` of an async `setup()` is safe.
 * In exchange, the caller owns the returned state's `release()` (see
 * AGENTS.md "Async setup() and Composable Lifecycles"):
 * - components register it with their releaser: `releaser.add(syncState(...))`;
 * - composables fold it into the `release()` / `ReleasableRef` they return;
 * - module-level singletons that must persist for the whole app lifetime
 *   simply never release it.
 */
import { effectScope, ref, watch, type Ref } from "vue";

import { browser } from "@/services/browser";

/*
 * Constants
 */

type SyncOptions = {
  load?: boolean;
  preset?: boolean;
  immediate?: boolean;
  deep?: boolean;
};

/**
 * A writable state ref persisted to a store; `release()` stops the
 * persistence watcher (the caller owns it — see the module doc above).
 */
export type SyncedState<T> = Ref<T> & { release: () => void };

const DEFAULT_OPTIONS: Required<SyncOptions> = {
  load: true,
  preset: true,
  immediate: false,
  deep: false,
};

/*
 * Functions
 */

export function useBrowserStore(namespace?: string) {
  function namespacedKey(key: string): string {
    return namespace ? `${namespace}/${key}` : key;
  }

  function syncState<T>(key: string, defaultValue: T, options?: SyncOptions) {
    const _options = { ...DEFAULT_OPTIONS, ...options };
    const identifier = namespacedKey(key);

    // Load initial value synchronously
    let initialValue: T = defaultValue;
    if (_options.load) {
      initialValue = browser.store.getJson(identifier, defaultValue);
    }

    // Save the initial value in case there wasn't any in the local
    // store.
    //
    // This ease the process of migrating because the user's preference
    // is already recorded in the local storage.
    if (_options.preset) {
      browser.store.setJson(identifier, initialValue);
    }

    // Initialize the state with the initial value
    const state = ref<T>(initialValue);

    // Set up watcher immediately to catch all changes. It runs in a
    // detached scope: never implicitly stopped by the calling component's
    // effect scope, only by the returned `release()`.
    const scope = effectScope(true);
    scope.run(() => {
      watch(
        state,
        (value) => {
          browser.store.setJson(identifier, value);
        },
        {
          immediate: _options.immediate,
          deep: _options.deep,
        }
      );
    });

    return Object.assign(state, { release: () => scope.stop() });
  }

  function syncRawState(
    key: string,
    defaultValue: string,
    options?: SyncOptions
  ): SyncedState<string> {
    const _options = { ...DEFAULT_OPTIONS, ...options };
    const identifier = namespacedKey(key);
    let initialValue = defaultValue;
    if (_options.load) {
      initialValue = browser.store.getRaw(identifier, defaultValue);
    }
    if (_options.preset) {
      browser.store.setRaw(identifier, initialValue);
    }
    const state = ref(initialValue);
    const scope = effectScope(true);
    scope.run(() => {
      watch(state, (value) => browser.store.setRaw(identifier, value), {
        immediate: _options.immediate,
        deep: _options.deep,
      });
    });
    return Object.assign(state, { release: () => scope.stop() });
  }

  function set<T>(key: string, value: T) {
    browser.store.setJson(namespacedKey(key), value);
  }

  function setRaw(key: string, value: string) {
    browser.store.setRaw(namespacedKey(key), value);
  }

  function get<T>(key: string, defaultValue: T): T {
    return browser.store.getJson(namespacedKey(key), defaultValue);
  }

  function getRaw(key: string, defaultValue: string): string {
    return browser.store.getRaw(namespacedKey(key), defaultValue);
  }

  function unset(key: string) {
    browser.store.unset(namespacedKey(key));
  }

  /**
   * One-time migration for a key that older app versions wrote without the
   * account namespace: move the un-namespaced value into this store's
   * namespace and delete the legacy key so it is never read again. When the
   * key was also renamed, pass the old name as `legacyKey`. The stored
   * string moves as-is, which preserves both raw and JSON encodings. No-op
   * when the store has no namespace, when the namespaced key already exists
   * (already migrated, or written since), or when there is no legacy value.
   * Call it before the first read of the key (e.g. before `syncState`,
   * whose `preset` would otherwise seed the default).
   */
  function migrate(key: string, legacyKey: string = key): void {
    if (!namespace) return;
    const identifier = namespacedKey(key);
    if (browser.store.has(identifier)) return;
    const legacy = browser.store.getRaw(legacyKey, null);
    if (legacy === null) return;
    browser.store.unset(legacyKey);
    browser.store.setRaw(identifier, legacy);
  }

  /**
   * One-time rename of a key that older app versions wrote under another
   * name *in this same store* (namespace included): move the value stored
   * under `legacyKey` to `key` and delete the legacy entry. This is
   * `migrate`'s sibling for keys that were already namespaced and only
   * changed names. Same rules: the stored string moves as-is, no-op when
   * `key` already has a value (already renamed, or written since) or when
   * there is no legacy value, and it must run before the first read of
   * `key` (e.g. before `syncState`, whose `preset` would otherwise seed the
   * default).
   */
  function rename(key: string, legacyKey: string): void {
    const identifier = namespacedKey(key);
    if (browser.store.has(identifier)) return;
    const legacyIdentifier = namespacedKey(legacyKey);
    const legacy = browser.store.getRaw(legacyIdentifier, null);
    if (legacy === null) return;
    browser.store.unset(legacyIdentifier);
    browser.store.setRaw(identifier, legacy);
  }

  function has(key: string): boolean {
    return browser.store.has(namespacedKey(key));
  }

  /**
   * All stored keys starting with `prefix`, relative to the namespace (the
   * returned keys can be passed back to `get`/`set`/`has` directly).
   */
  function keysWithPrefix(prefix: string): string[] {
    const namespaced = namespacedKey(prefix);
    const strip = namespaced.length - prefix.length;
    return browser.store
      .keysWithPrefix(namespaced)
      .map((key) => key.slice(strip));
  }

  return {
    namespacedKey,
    migrate,
    rename,
    syncState,
    syncRawState,
    set,
    setRaw,
    get,
    getRaw,
    unset,
    has,
    keysWithPrefix,
  };
}
